import { jsx as f } from '../../jsx-runtime'
import type { FNode, FNodeChild } from '../../dom/types'
import { RouterCtx, RouteDepthCtx, useLocation } from '../router'
import { createRoutesFromChildren, matchRoutes } from '../utils'
import { Route } from './Route'

function isFNode(node: unknown): node is FNode {
    return node !== null && typeof node === 'object' && !Array.isArray(node) && 'type' in node
}

// Generate a stable route key based on the matched route path pattern and params
function generateRouteKey(match: { route: { path: string }, params: Record<string, string> }): string {
    // Use the route path pattern + serialized params for a stable key
    const paramStr = Object.entries(match.params)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([k, v]) => `${k}=${v}`)
        .join('&')
    return `route:${match.route.path}${paramStr ? '?' + paramStr : ''}`
}

export function Routes(props: { children: FNodeChild }) {
    const [currentLocation, navigate] = useLocation()

    // Parse children to find <Route> definitions and other content
    const childrenList: FNodeChild[] = Array.isArray(props.children) ? props.children : [props.children]

    // Find routes for matching (but keep order with other children)
    const routeNodes = childrenList.filter(child => isFNode(child) && child.type === Route)
    // Track first Route index to insert matched content there
    const firstRouteIndex = childrenList.findIndex(child => isFNode(child) && child.type === Route)

    // Other children (non-Route) keep their positions relative to routes
    let beforeRouteChildren: FNodeChild[] = []
    let afterRouteChildren: FNodeChild[] = []

    if (firstRouteIndex === -1) {
        // No Route children - all are "other" children
        beforeRouteChildren = childrenList.filter(child => !isFNode(child) || child.type !== Route)
    } else {
        // Split at first Route position
        beforeRouteChildren = childrenList.slice(0, firstRouteIndex).filter(child => !isFNode(child) || child.type !== Route)
        afterRouteChildren = childrenList.slice(firstRouteIndex).filter(child => !isFNode(child) || child.type !== Route)
    }

    // Create route definitions
    const routeDefinitions = createRoutesFromChildren(routeNodes)

    // DIRECT access to currentLocation.pathname
    // This should trigger reactive tracking in the component's effect context
    const currentPath = currentLocation.pathname
    const matches = matchRoutes(routeDefinitions, currentPath) || []
    const params = matches.length > 0 ? matches[matches.length - 1].params : {}

    const routerContext = {
        location: currentLocation,
        navigate,
        matches: matches,
        params: params
    }

    // Render matched component
    let matchedContent: FNodeChild = null

    if (matches.length > 0) {
        const rootMatch = matches[0]
        const Component = rootMatch.route.component

        // Generate a unique and stable key for this route
        // This ensures proper cleanup when navigating between routes
        const routeKey = generateRouteKey(rootMatch)

        // Guard Check
        if (rootMatch.route.beforeEnter) {
            const result = rootMatch.route.beforeEnter(rootMatch.params)
            if (result !== false) {
                // Wrap in a keyed container to ensure proper cleanup
                matchedContent = f(RouteDepthCtx.Provider, {
                    value: 1,
                    key: routeKey,
                    children: f(Component, { params: rootMatch.params })
                })
            }
        } else {
            // Wrap in a keyed container to ensure proper cleanup
            matchedContent = f(RouteDepthCtx.Provider, {
                value: 1,
                key: routeKey,
                children: f(Component, { params: rootMatch.params })
            })
        }
    }

    return f(RouterCtx.Provider, {
        value: routerContext,
        children: [...beforeRouteChildren, matchedContent, ...afterRouteChildren]
    })
}
